import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  CurrencyDollarIcon,
  UserGroupIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormClearErrors,
  UseFormSetError,
  UseFormWatch
} from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ListingFormData, ListingIntent } from '../types';
import LocationInput from './LocationInput';
import { db } from '../../../auth/firebaseConfig';
import { remoteConfig } from '../../../utility/firebaseRemoteConfigHelper';
import {
  getHicpForDate,
  calculateRentIncrease
} from '../../../utility/rentReviewCalculator';
import { subtractMonthsSafe } from '../../../utility/dateHelpers';

interface RpzCalculationResult {
  isCompliant: boolean;
  maxLegalRent: number;
  explanation: string;
  yearsElapsed: number;
  appliedRate: number; // percent per annum actually applied (min of cap vs HICP)
}

interface PropertyDetailsProps {
  register: UseFormRegister<ListingFormData>;
  errors: FieldErrors<ListingFormData>;
  setValue: UseFormSetValue<ListingFormData>;
  clearErrors: UseFormClearErrors<ListingFormData>;
  setError: UseFormSetError<ListingFormData>;
  watch: UseFormWatch<ListingFormData>;
  listingIntent: ListingIntent;
  locationInput: string;
  setLocationInput: (value: string) => void;
}

const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  register,
  errors,
  setValue,
  clearErrors,
  setError,
  watch,
  listingIntent,
  locationInput,
  setLocationInput
}) => {
  const priceLabel = listingIntent === 'sale' ? 'Price' : 'Price (per month)';

  // Watch form fields for RPZ calculation
  const isRentReview = watch('isRentReview');
  const currentRentAmount = watch('currentRentAmount');
  const lastRentSetDate = watch('lastRentSetDate');
  const proposedPrice = watch('price');

  // State for RPZ calculation result
  const [rpzResult, setRpzResult] = useState<RpzCalculationResult | null>(null);
  const [showRpzCalculation, setShowRpzCalculation] = useState(false);

  // Track previous RPZ error state to prevent infinite loops
  const previousRpzErrorRef = useRef<boolean>(false);

  // Calculate RPZ maximum rent when relevant fields change
  useEffect(() => {
    const run = async () => {
      if (
        listingIntent === 'rent' &&
        isRentReview &&
        currentRentAmount &&
        currentRentAmount > 0 &&
        lastRentSetDate
      ) {
        try {
          await remoteConfig.ensureInitialized?.();
          const capPerYear = remoteConfig.getRpzAnnualRentCapPercentage
            ? remoteConfig.getRpzAnnualRentCapPercentage()
            : 2;

          const rawLastDate = lastRentSetDate;
          const rawNewDate = new Date();
          const adjLastDate = subtractMonthsSafe(rawLastDate, 2);
          const adjNewDate = subtractMonthsSafe(rawNewDate, 2);

          const oldHicp = await getHicpForDate(db, adjLastDate);
          const newHicp = await getHicpForDate(db, adjNewDate);
          if (oldHicp == null || newHicp == null) {
            setRpzResult(null);
            setShowRpzCalculation(false);
            return;
          }

          const calc = calculateRentIncrease(
            {
              currentRent: currentRentAmount,
              lastSetDate: rawLastDate,
              newSetDate: rawNewDate,
              capPercentPerYear: capPerYear
            },
            oldHicp,
            newHicp
          );

          const maxLegalRent = Number(
            (currentRentAmount + calc.increaseAmount).toFixed(2)
          );
          const isCompliant =
            typeof proposedPrice === 'number'
              ? proposedPrice <= maxLegalRent
              : true;

          const months =
            (new Date().getFullYear() - lastRentSetDate.getFullYear()) * 12 +
            (new Date().getMonth() - lastRentSetDate.getMonth());
          const yearsElapsed = Math.max(0, months) / 12;

          const explanation = `We compared the HICP change (${calc.hicpChangePercent.toFixed(
            1
          )}%) with the RPZ cap (${capPerYear}% per annum pro‑rata over ${yearsElapsed.toFixed(
            1
          )} years = ${calc.capPercentApplied.toFixed(
            1
          )}%). The lower value (${calc.appliedPercent.toFixed(
            1
          )}%) was applied. Note: the HICP used is the most recently available on the date of the previous rent setting.`;

          const result: RpzCalculationResult = {
            isCompliant,
            maxLegalRent,
            explanation,
            yearsElapsed,
            appliedRate: capPerYear
          };

          setRpzResult(result);
          setShowRpzCalculation(true);

          const hasRpzViolation =
            proposedPrice && typeof proposedPrice === 'number'
              ? proposedPrice > result.maxLegalRent
              : false;

          if (hasRpzViolation && !previousRpzErrorRef.current) {
            setError('price', {
              type: 'rpzViolation',
              message: `Rent exceeds RPZ legal limit of €${result.maxLegalRent.toFixed(
                2
              )}`
            });
            previousRpzErrorRef.current = true;
          } else if (!hasRpzViolation && previousRpzErrorRef.current) {
            if (errors.price?.type === 'rpzViolation') {
              clearErrors('price');
            }
            previousRpzErrorRef.current = false;
          }
        } catch (error) {
          console.error('RPZ calculation error:', error);
          setRpzResult(null);
          setShowRpzCalculation(false);
        }
      } else {
        setRpzResult(null);
        setShowRpzCalculation(false);
        if (
          previousRpzErrorRef.current &&
          errors.price?.type === 'rpzViolation'
        ) {
          clearErrors('price');
          previousRpzErrorRef.current = false;
        }
      }
    };
    run();
  }, [
    isRentReview,
    currentRentAmount,
    lastRentSetDate,
    proposedPrice,
    listingIntent,
    setError,
    clearErrors,
    errors.price
  ]);

  const handleLocationChange = (value: string) => {
    setLocationInput(value);
    setValue('location', value, { shouldValidate: true });
    if (value.trim()) {
      clearErrors('location');
    }
  };

  return (
    <motion.div
      key="singleStep2"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      {/* Progress indicator */}
      <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4 rounded-r-lg mb-6">
        <p className="text-sm text-indigo-800">
          <strong>Step 2 of 6:</strong> Property details • Almost there!{' '}
          <strong>~90 seconds</strong> remaining
        </p>
      </div>

      <h2 className="text-2xl font-semibold mb-6 flex items-center">
        <CurrencyDollarIcon className="w-6 h-6 mr-2 text-blue-500" />
        Property Details
      </h2>
      <div className="space-y-6">
        {/* RPZ Checkbox - Only for Rent Listings */}
        {listingIntent === 'rent' && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="isRentReview"
                {...register('isRentReview')}
                className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <label
                  htmlFor="isRentReview"
                  className="font-medium text-gray-900 cursor-pointer"
                >
                  Are you increasing rent for a current tenant?
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  <InformationCircleIcon className="w-4 h-4 inline-block mr-1" />
                  All of Ireland is a Rent Pressure Zone (RPZ). If you're
                  raising rent for an existing tenancy, check this box to verify
                  your proposed rent complies with the legal cap.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Conditional RPZ Fields */}
        {listingIntent === 'rent' && isRentReview && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg"
          >
            <h3 className="font-semibold text-gray-900 flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 mr-2 text-yellow-600" />
              RPZ Compliance Check
            </h3>
            <p className="text-sm text-gray-700">
              To comply with Irish law, provide the current rent details so we
              can calculate the maximum legal rent increase.
            </p>

            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900">
              <p className="font-semibold mb-1">
                ⚠️ Important Legal Disclaimer
              </p>
              <p className="leading-relaxed">
                This calculation is provided for <strong>guidance only</strong>{' '}
                and does not constitute legal advice. While we use the current
                RPZ cap rate, your specific situation may involve additional
                legal considerations. RentSmart.ie is not liable for any legal
                consequences arising from rent increases. We strongly recommend
                consulting the{' '}
                <a
                  href="https://www.rtb.ie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-amber-700 font-medium"
                >
                  Residential Tenancies Board (RTB)
                </a>{' '}
                or a qualified solicitor before proceeding with any rent
                increase.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Current Rent Amount (€/month){' '}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                    €
                  </span>
                  <input
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    {...register('currentRentAmount', {
                      required: isRentReview
                        ? 'Current rent amount is required for rent reviews'
                        : false,
                      min: {
                        value: 1,
                        message: 'Current rent must be at least €1'
                      },
                      valueAsNumber: true
                    })}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:outline-none transition duration-300 ${
                      errors.currentRentAmount
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    placeholder="e.g., 1000"
                  />
                </div>
                {errors.currentRentAmount && (
                  <span className="text-red-500 text-sm">
                    {errors.currentRentAmount.message}
                  </span>
                )}
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Date Last Rent Was Set <span className="text-red-500">*</span>
                </label>
                <input
                  type="hidden"
                  {...register('lastRentSetDate', {
                    required: isRentReview
                      ? 'Date last rent was set is required for rent reviews'
                      : false,
                    validate: {
                      notFuture: (value) => {
                        if (!isRentReview || !value) return true;
                        return (
                          value <= new Date() || 'Date cannot be in the future'
                        );
                      }
                    }
                  })}
                />
                <DatePicker
                  selected={lastRentSetDate}
                  onChange={(date: Date | null) => {
                    if (date) {
                      setValue('lastRentSetDate', date, {
                        shouldValidate: true
                      });
                    }
                  }}
                  maxDate={new Date()} // Cannot be in future
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select date"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:outline-none transition duration-300 ${
                    errors.lastRentSetDate
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                  required={isRentReview}
                />
                {errors.lastRentSetDate && (
                  <span className="text-red-500 text-sm">
                    {errors.lastRentSetDate.message}
                  </span>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  When was the current rent of €
                  {currentRentAmount?.toFixed(2) || '0.00'} last set?
                </p>
              </div>
            </div>
          </motion.div>
        )}

        <div>
          <label className="block mb-2 font-medium text-gray-700">
            {priceLabel} <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Set a competitive price to attract quality{' '}
            {listingIntent === 'sale' ? 'buyers' : 'tenants'}
          </p>

          {/* RPZ Calculation Display */}
          {showRpzCalculation && rpzResult && (
            <div
              className={`mb-3 p-3 rounded-lg border-2 ${
                rpzResult.isCompliant
                  ? 'bg-green-50 border-green-400'
                  : 'bg-red-50 border-red-400'
              }`}
            >
              <div className="flex items-start">
                {rpzResult.isCompliant ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                ) : (
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p
                    className={`font-semibold text-sm ${
                      rpzResult.isCompliant ? 'text-green-900' : 'text-red-900'
                    }`}
                  >
                    {rpzResult.isCompliant
                      ? 'RPZ Compliant ✓'
                      : '⚠️ RPZ Violation Detected'}
                  </p>
                  <p
                    className={`text-xs mt-1 ${
                      rpzResult.isCompliant ? 'text-green-800' : 'text-red-800'
                    }`}
                  >
                    Maximum Legal Rent: €{rpzResult.maxLegalRent.toFixed(2)}
                  </p>
                  <details className="mt-2">
                    <summary className="text-xs cursor-pointer text-gray-700 hover:text-gray-900">
                      View calculation details
                    </summary>
                    <p className="text-xs mt-2 text-gray-700 leading-relaxed">
                      {rpzResult.explanation}
                    </p>
                    <div className="mt-2 text-xs text-gray-600">
                      <p>
                        • Time elapsed: {rpzResult.yearsElapsed.toFixed(1)}{' '}
                        years
                      </p>
                      <p>• Applied rate: {rpzResult.appliedRate}% per annum</p>
                    </div>
                  </details>
                </div>
              </div>
            </div>
          )}

          <div className="relative">
            <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
              €
            </span>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              {...register('price', {
                required: 'Price is required',
                min: { value: 1, message: 'Price must be at least €1' },
                valueAsNumber: true,
                validate: {
                  rpzCompliance: (value) => {
                    // Only validate RPZ compliance if it's a rent review
                    if (
                      listingIntent === 'rent' &&
                      isRentReview &&
                      rpzResult &&
                      value
                    ) {
                      return (
                        value <= rpzResult.maxLegalRent ||
                        `Rent exceeds RPZ legal limit of €${rpzResult.maxLegalRent.toFixed(2)}. Please adjust to comply with Irish law.`
                      );
                    }
                    return true;
                  }
                },
                onChange: (e) => {
                  const value = e.target.valueAsNumber;
                  if (value === 0 || isNaN(value)) {
                    setValue('price', undefined);
                  }
                }
              })}
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
                errors.price ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., 1200"
            />
          </div>
          {errors.price && (
            <span className="text-red-500 text-sm flex items-start mt-1">
              <ExclamationTriangleIcon className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
              <span>{errors.price.message}</span>
            </span>
          )}

          {/* Helpful hint for non-compliant rent */}
          {showRpzCalculation &&
            rpzResult &&
            !rpzResult.isCompliant &&
            proposedPrice && (
              <div className="mt-2 bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                <p className="text-sm text-blue-900">
                  <InformationCircleIcon className="w-4 h-4 inline-block mr-1" />
                  <strong>Tip:</strong> To comply with RPZ rules, reduce the
                  rent to €{rpzResult.maxLegalRent.toFixed(2)} or less. This
                  protects you from legal issues and ensures your listing
                  remains active.
                </p>
              </div>
            )}
        </div>

        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Location <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Enter the Irish location (e.g., Dublin 2, Cork City, Galway)
          </p>
          <LocationInput
            value={locationInput}
            onChange={handleLocationChange}
            error={errors.location?.message}
            placeholder="e.g., Dublin City Centre, Cork, Galway"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block mb-2 font-medium text-gray-700">
              <UserGroupIcon className="w-5 h-5 inline-block mr-1 text-blue-500" />
              Bedrooms <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              {...register('bedrooms', {
                required: 'Number of bedrooms is required',
                min: { value: 1, message: 'At least 1 bedroom required' },
                validate: {
                  integer: (value) =>
                    Number.isInteger(Number(value)) || 'Must be an integer'
                }
              })}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
                errors.bedrooms ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., 2"
            />
            {errors.bedrooms && (
              <span className="text-red-500 text-sm">
                {errors.bedrooms.message}
              </span>
            )}
          </div>
          <div>
            <label className="block mb-2 font-medium text-gray-700">
              Bathrooms <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              {...register('bathrooms', {
                required: 'Number of bathrooms is required',
                min: {
                  value: 1,
                  message: 'At least 1 bathroom required'
                },
                validate: {
                  integer: (value) =>
                    Number.isInteger(Number(value)) || 'Must be an integer'
                }
              })}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
                errors.bathrooms ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., 1"
            />
            {errors.bathrooms && (
              <span className="text-red-500 text-sm">
                {errors.bathrooms.message}
              </span>
            )}
          </div>
          <div>
            <label className="block mb-2 font-medium text-gray-700">
              Size (m²)
            </label>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              {...register('size', {
                min: { value: 1, message: 'Size must be at least 1 sq meter' },
                valueAsNumber: true,
                onChange: (e) => {
                  const value = e.target.valueAsNumber;
                  if (value === 0 || isNaN(value)) {
                    setValue('size', undefined);
                  }
                }
              })}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
                errors.size ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., 85"
            />
            {errors.size && (
              <span className="text-red-500 text-sm">
                {errors.size.message}
              </span>
            )}
          </div>
        </div>

        {/* Help text */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <p className="text-sm text-blue-800">
            <strong>💡 Tip:</strong> Properties with accurate details get{' '}
            <strong>3x more inquiries</strong> from interested{' '}
            {listingIntent === 'sale' ? 'buyers' : 'tenants'}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default PropertyDetails;
